import packageJSON from '../../package.json';
import { RootState } from '../app/store';
import {
  IssueReportAnchor,
  IssueReportCarts,
  IssueReportContext,
  IssueReportEnvelopeIdentity,
  IssueReportOrderRequest,
  IssueReportTranscriptLine,
} from '../redux/features/issueReport/issueReport.props';
import {
  selectAssignedTaskId,
  selectNavigationViaTaskRouter,
} from '../redux/features/taskRouter/taskRouter.selector';
import {
  selectAwaitingResponseSinceMs,
  selectTranscriptMessages,
} from '../selectors/message';
import { IBrandMenuOrderItem } from '../types/brandMenu';
import { getConsolidatedMenuBrand } from './restaurants';
import { buildUnifiedMenuModuleOrder } from './menuUnification';

/**
 * HITL-1492 Pillar 3, Part A — build the frozen issue-report context.
 *
 * PURE and SYNCHRONOUS: no `await`, no network, no DOM. That is what satisfies
 * NFR-1 — the press handler does O(1) work and the flag never costs a frame.
 *
 * Captured ONCE on press and never re-read. That is mandatory, not stylistic:
 * `navigateToTaskRouterPage` dispatches closeConnection(), resetAI() and
 * resetSelectedRestaurantDetails() before navigating, so by the time a
 * classification is submitted at the task-router boundary the socket is closed and
 * store_code is gone from Redux.
 */

/** ±5 turns around the tail of the transcript. */
export const TRANSCRIPT_WINDOW_TURNS = 5;
/** Per-line character cap. Longer payloads are truncated and marked. */
export const TRANSCRIPT_LINE_CHAR_CAP = 500;
/** Total character budget across the window. */
export const TRANSCRIPT_TOTAL_CHAR_CAP = 8 * 1024;
/** Per-cart item cap. */
export const CART_ITEM_CAP = 60;

export const AUDIO_CLIP_PRE_MS = 15000;
export const AUDIO_CLIP_POST_MS = 5000;

/**
 * The POS check-item keys worth shipping.
 *
 * `checkEventItems` is POSMON's raw shape — ~24 keys per item, most of them empty
 * scaffolding (hold_uid, hold_expire_time, memo_id, screen_group_id,
 * price_description_id, custom_request_pos_id, source_check_id). A six-item order is
 * ~150 keys of mostly nothing and it is the largest block in the payload.
 *
 * This is a KEY PROJECTION, not a transformation: values are copied verbatim, so the
 * decision to diff the two carts server-side is untouched. Unknown keys are dropped
 * rather than preserved — if a diff later needs one, add it here.
 */
export const POS_CART_PROJECTED_KEYS = [
  'name',
  'pos_id',
  'price',
  'tax',
  'is_combo',
  'is_modifier',
  'is_coupon',
  'is_86ed',
] as const;

export interface BuildIssueReportContextDeps {
  /**
   * Injected so every offset in the payload is deterministic under test, and so the
   * builder is a pure function of its arguments rather than of the wall clock.
   */
  nowMs?: number;
}

const toEpoch = (iso?: string | null): number | null => {
  if (!iso) return null;
  const epoch = new Date(iso).getTime();
  return Number.isNaN(epoch) ? null : epoch;
};

const projectPosItem = (item: any): Record<string, unknown> =>
  POS_CART_PROJECTED_KEYS.reduce((acc, key) => {
    if (item && key in item) {
      acc[key] = item[key];
    }
    return acc;
  }, {} as Record<string, unknown>);

/**
 * The ±5-turn window around the tail, capped per line and in total.
 *
 * Trimmed rather than raw because this is the largest variable-length block in the
 * payload and the pagehide path shares a 64 KB keepalive budget across all in-flight
 * requests. Truncation is marked so a consumer never mistakes a cut line for a short
 * utterance.
 */
const buildTranscriptWindow = (
  messages: any[]
): IssueReportTranscriptLine[] => {
  const window = messages.slice(-(TRANSCRIPT_WINDOW_TURNS * 2 + 1));
  let budget = TRANSCRIPT_TOTAL_CHAR_CAP;
  const lines: IssueReportTranscriptLine[] = [];

  // Newest-first so the budget is spent on the lines nearest the flag, then restore
  // chronological order — a window whose OLDEST lines survive is the wrong window.
  for (let i = window.length - 1; i >= 0; i -= 1) {
    if (budget <= 0) break;
    const frame = window[i];
    const rawPayload =
      typeof frame?.data?.payload === 'string' ? frame.data.payload : '';
    const perLineCap = Math.min(TRANSCRIPT_LINE_CHAR_CAP, budget);
    const payload = rawPayload.slice(0, perLineCap);
    budget -= payload.length;

    lines.unshift({
      frame_id: frame?.id ?? '',
      agent_type: frame?.agent_type ?? '',
      status: frame?.data?.status ?? '',
      payload,
      timestamp: frame?.timestamp ?? '',
      ...(frame?.isPromoted ? { is_promoted: true } : null),
      ...(payload.length < rawPayload.length ? { truncated: true } : null),
    });
  }

  return lines;
};

const buildAnchor = (
  state: RootState,
  messages: any[],
  nowMs: number
): IssueReportAnchor => {
  const newestFrame = messages.length ? messages[messages.length - 1] : null;

  // No single reliable session start exists, so the zero point is named rather than
  // assumed. Without the label `offset_ms` is uninterpretable — 74310 ms from what?
  const taskRouterStartedAt = toEpoch(
    state.taskRouter?.taskAssignedSessionStartedAt
  );
  const firstFrameAt = messages.length ? toEpoch(messages[0]?.timestamp) : null;

  const [originMs, offsetFrom] = taskRouterStartedAt
    ? ([taskRouterStartedAt, 'task_router_session_started_at'] as const)
    : firstFrameAt
    ? ([firstFrameAt, 'first_transcript_frame'] as const)
    : ([null, 'none'] as const);

  return {
    frame_id: newestFrame?.id ?? null,
    at_ms: nowMs,
    offset_ms: originMs === null ? null : Math.max(0, nowMs - originMs),
    offset_from: offsetFrom,
  };
};

const buildCarts = (state: RootState): IssueReportCarts => {
  const cartItems = state.cart?.cartItems || {};
  const cartItemsQuantity = state.cart?.cartItemsQuantity || {};

  // Same converter the brand-menu order submission and the agent_cart event use, so
  // the snapshot is 1:1 with a submitted order.
  const agentCartAll = Object.values(cartItems)
    .map((item: any) => buildUnifiedMenuModuleOrder(item, cartItemsQuantity))
    .filter((item): item is IBrandMenuOrderItem => Boolean(item));
  const posCartAll: any[] = state.order?.checkEventItems || [];

  return {
    agent_cart: agentCartAll.slice(0, CART_ITEM_CAP),
    pos_cart: posCartAll.slice(0, CART_ITEM_CAP).map(projectPosItem),
    truncated:
      agentCartAll.length > CART_ITEM_CAP || posCartAll.length > CART_ITEM_CAP,
  };
};

const buildLatestOrderRequest = (
  state: RootState,
  nowMs: number
): IssueReportOrderRequest | null => {
  const { requestsById = {}, requestsOrder = [] } = state.order || ({} as any);
  const lastRequestId = requestsOrder.length
    ? requestsOrder[requestsOrder.length - 1]
    : '';
  const entry: any = lastRequestId ? requestsById[lastRequestId] : undefined;
  if (!entry) return null;

  const sentAt = toEpoch(entry.orderSentAt);
  const checkReceivedAt = toEpoch(entry.checkResponse?.timestamp);

  return {
    request_id: entry.id ?? null,
    order_id: entry.orderId ?? null,
    // The check message's envelope id, matching how the order-processing metric
    // resolves it (orderSlice.utils buildAndDispatchOrderProcessingMetric).
    check_id: entry.checkResponse?.id ?? null,
    final: Boolean(entry.isFinal),
    sent_at: entry.orderSentAt ?? null,
    status: entry.status ?? null,
    // Order -> check round trip once the check has landed; otherwise how long the
    // order has been outstanding, which is the interesting number for a POS report.
    round_trip_ms:
      sentAt === null ? null : Math.max(0, (checkReceivedAt ?? nowMs) - sentAt),
  };
};

/**
 * Store CONFIGURATION, not live control state.
 *
 * Says strictly more than the `isUnsupervisedAi` boolean it replaces: it separates
 * "this restaurant has no AI at all" from "AI runs with a human watching". Compare
 * `ai.is_ai_auto_mode`, which is who is driving right now — `supervised` with
 * `is_ai_auto_mode: false` is a takeover in progress.
 */
const resolveAiMode = (
  state: RootState
): 'pure_hitl' | 'supervised' | 'unsupervised' => {
  if (!state.restaurant?.isAIEnabled) return 'pure_hitl';
  return state.restaurant?.selectedRestaurantDetails?.isUnsupervisedAi
    ? 'unsupervised'
    : 'supervised';
};

/**
 * The envelope identity, frozen at press time.
 *
 * `session_id` mirrors messagingTransformMiddleware's OWN fallback chain rather than
 * using selectSessionId, which is only step 2 of it — the chain below is the one that
 * actually stamps outbound events.
 *
 * Frozen rather than read live at send time because navigateToTaskRouterPage wipes all
 * three before a task-router-boundary classification is submitted. Kept out of the
 * emitted `context` on purpose: these become envelope fields, so duplicating them in
 * the payload would send each one twice.
 */
export const buildIssueReportEnvelopeIdentity = (
  state: RootState
): IssueReportEnvelopeIdentity => ({
  session_id:
    state.taskRouter?.taskAssignedSessionId ||
    state.sessionBoundary?.sessionId ||
    state.sessionBoundary?.endedSessionId ||
    state.messages?.currentSessionId ||
    null,
  restaurant_code:
    state.restaurant?.selectedRestaurantDetails?.restaurantCode || null,
  lane_id: state.taskRouter?.taskAssignedLaneId || null,
});

export const buildIssueReportContext = (
  state: RootState,
  deps: BuildIssueReportContextDeps = {}
): IssueReportContext => {
  const nowMs = deps.nowMs ?? Date.now();

  // What the agent is actually looking at: this selector already swaps customer
  // lines to `asr_promoted_text` when secondary ASR is on, so the window is the
  // rendered transcript rather than a second reconstruction of it.
  const messages: any[] = selectTranscriptMessages(state) || [];

  const restaurantDetails = state.restaurant?.selectedRestaurantDetails;
  const restaurantCode = restaurantDetails?.restaurantCode || '';
  const primaryStoreCode = restaurantDetails?.primaryRestaurantCode;

  const awaitingSinceMs = selectAwaitingResponseSinceMs(state);
  const staffInterventionAt = toEpoch(state.messages?.staffInterventionAt);

  return {
    session: {
      session_status: state.sessionBoundary?.sessionStatus ?? '',
      session_start_reason: state.sessionBoundary?.sessionStartReason ?? '',
      session_end_reason: state.sessionBoundary?.sessionEndReason ?? '',
      is_car_present: Boolean(state.messages?.isCarPresent),
    },
    restaurant: {
      ...(primaryStoreCode && primaryStoreCode !== restaurantCode
        ? { primary_store_code: primaryStoreCode }
        : null),
      // DERIVED. null means EITHER "no brand matched" OR "restaurant is excluded" —
      // an excluded DQ store reports null, so reading that as "not DQ" is wrong.
      brand: getConsolidatedMenuBrand(
        restaurantCode,
        state.config?.CONSOLIDATED_MENU_BRANDS || [],
        state.config?.CONSOLIDATED_MENU_EXCLUDED_RESTAURANTS || []
      ),
      ai_mode: resolveAiMode(state),
      timezone: restaurantDetails?.timezone ?? null,
    },
    // ACTIVE language, not the store's bilingual capability: for an ASR or audio
    // report the language actually being spoken is the field that matters.
    language: state.language?.currentLanguage ?? '',
    task_router: {
      via_task_router: Boolean(selectNavigationViaTaskRouter(state)),
      task_id: selectAssignedTaskId(state) ?? null,
    },
    ai: {
      // READ ONLY. Mutating isAIAutoMode to fix a display symptom caused a prod
      // incident: dropped orders and reverted carts (MR 1534).
      is_ai_auto_mode: Boolean(state.ai?.isAIAutoMode),
      // The colour only (AIStatusColors: green|yellow|orange|red). `aiStatus` is
      // `{ status, message }` and the message is a display string.
      ai_status: state.ai?.aiStatus?.status ?? null,
      is_escalation: Boolean(state.ai?.isAIEscalation),
      escalation_reason: state.ai?.aiEscalationReason ?? null,
      requires_takeover: Boolean(state.ai?.requiresTakeover),
      turn: state.ai?.turn ?? 0,
      sentiment_label: state.ai?.sentimentLabel ?? '',
      express_mode_active: Boolean(state.ai?.expressModeActive),
    },
    menu: {
      is_brand_menu: Boolean(state.menu?.brandMenu),
      brand_menu_version:
        state.menu?.loadedVersionDetails?.brandMenuVersion ?? null,
      prp_menu_version:
        state.menu?.loadedVersionDetails?.prpMenuVersion ?? null,
    },
    pos: {
      order_error: state.order?.orderError || null,
      final_order_status: state.order?.finalOrderStatus ?? null,
      menu_module_cart_errors: state.order?.menuModuleCartErrors || [],
      latest_order_request: buildLatestOrderRequest(state, nowMs),
    },
    carts: buildCarts(state),
    transcript_window: buildTranscriptWindow(messages),
    anchor: buildAnchor(state, messages, nowMs),
    signals: {
      // Offsets, never raw epochs: HITL-1500's ranker must be a pure function of the
      // context that never calls a clock, or the recorded candidates could drift from
      // the rendered chips.
      awaiting_response_ms:
        awaitingSinceMs === null ? null : Math.max(0, nowMs - awaitingSinceMs),
      ms_since_staff_intervention:
        staffInterventionAt === null
          ? null
          : Math.max(0, nowMs - staffInterventionAt),
      has_customer_final_this_session: messages.some(
        (frame) =>
          frame?.data?.status !== 'TTS' &&
          frame?.data?.status !== 'hypothesis' &&
          typeof frame?.data?.payload === 'string' &&
          frame.data.payload.trim() !== ''
      ),
      auto_combo_applied: Boolean(state.autoCombo?.combinedCartItemIds?.length),
    },
    ws: {
      event_reconnects: state.messages?.wsReconnects?.event ?? 0,
      audio_reconnects: state.messages?.wsReconnects?.audio ?? 0,
    },
  };
};

/**
 * The clip window — coordinates live in `context.anchor`.
 *
 * No audio is retained client-side (guest PCM frames are dropped immediately in the
 * middleware; the worklet buffer is a jitter buffer), so this exists purely so clips
 * stay retroactively cuttable server-side. Null when there is no usable offset,
 * since there is then nothing to cut against.
 */
export const buildAudioWindow = (
  context: IssueReportContext
): { pre: number; post: number } | null =>
  context.anchor.offset_ms === null
    ? null
    : { pre: AUDIO_CLIP_PRE_MS, post: AUDIO_CLIP_POST_MS };

export const ISSUE_REPORT_CLIENT_VERSION = packageJSON.version;
